'use strict';
import {Observable, of} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {EnvService} from './env.service';
import {RouterPath} from "../../shared/models";
import { ToastService } from '../services/toast.service';
import {AppStateService} from '../services/app-state.service';
import {TranslateService} from '@ngx-translate/core';
import {LocaleService} from '../services/locale.service';
import {UserSessionService} from '../services/user-session.service';

@Injectable({providedIn: 'root'})
export class RestConnector {
    constructor(
        private httpClient: HttpClient,
        private router: Router,
        private env: EnvService,
        private toast: ToastService,
        private appStateService: AppStateService,
        private translate: TranslateService,
        private localeService: LocaleService,
        private userSession: UserSessionService,
    ) {
    }

    /**
     * Builds HTTP options for a request.
     * Default headers: Accept, Access-Control-Allow-Origin, accept-language, Content-Type: 'application/json',
     * and token (if present in localStorage). Optional `headers` overrides or adds to these (e.g. headers = { 'Content-Type': 'blob' }).
     * @param params - Query params object. Pairs whose value is null or undefined are omitted.
     * @param headers - Optional headers to merge over defaults (overwrites default headers with same key).
     * @param responseType - Optional response type (e.g. 'blob').
     */
    public getHttpOptions(params?: any, headers?: Record<string, string>, responseType?: any) {
        const defaultHeaders: Record<string, string> = {
            'Accept': 'application/json, multipart/form-data',
            'accept-language': 'en',
            'Content-Type': 'application/json'
        };
        const token = localStorage.getItem('token');
        if (token) {
            defaultHeaders['token'] = token;
        }
        const merged = headers ? { ...defaultHeaders, ...headers } : defaultHeaders;
        let httpHeaders = new HttpHeaders();
        for (const [key, value] of Object.entries(merged)) {
            if (value != null && value !== '') {
                httpHeaders = httpHeaders.set(key, value);
            }
        }
        const httpOptions = {
            headers: httpHeaders,
            params: new HttpParams(),
            responseType: responseType
        };
        if (params) {
            for (const [key, value] of Object.entries(params)) {
                if (value !== null && value !== undefined && value !== '') {
                    httpOptions.params = httpOptions.params.append(key, String(value));
                }
            }
        }
        return httpOptions;
    }

    /**
     * Handles error codes returned in response body (HTTP 200 with { status: false, httpCode: 401/403/500 })
     * Some backend APIs return errors this way instead of using proper HTTP status codes.
     * @returns null if error was handled (to prevent further processing), undefined otherwise
     */
    handleResponseBodyError(resp: any): any {
        if (!resp || resp.status) {
            return resp;
        }

        if (resp.httpCode == 401) {
            this.endExpiredSession();
            return null;
        }
        if (resp.httpCode == 403) {
            if (!resp.errorCode) {
                const errorMsg = this.translate.instant('notifications.common.noPermission');
                this.toast.error(errorMsg);
            }
            return resp;
        }
        if (resp.httpCode == 500) {
            const errorMsg = this.translate.instant('notifications.common.somethingWentWrong');
            this.toast.error(errorMsg);
            return resp;
        }
        return resp;
    }

    /**
     * 401: wipe the user's data. Reload only when a user was logged in, so a guest hitting a 401
     * on page load can't end up in a reload loop (and guest signup/OTP data in sessionStorage stays).
     */
    private endExpiredSession(): void {
        if (localStorage.getItem('token')) {
            this.userSession.clearAndReload();
            return;
        }
        this.userSession.clearLocal();
        this.appStateService.nextTokenValue(true);
        this.localeService.navigate(RouterPath.HOME);
    }

    handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // Client-side errors
            console.error(`Error: ${error.error.message}`);
            return of(null);
        }

        // Server-side errors
        console.error(`Error code: ${error.status}\nMessage: ${error.message}`);

        switch (error.status) {
            case 401:
                this.endExpiredSession();
                return of(null);
            case 403:
                const forbiddenMsg = this.translate.instant('notifications.common.noPermission');
                this.toast.error(forbiddenMsg);
                return of(null);
            case 405: {
                const methodNotAllowedMsg = this.translate.instant('notifications.common.methodNotAllowed');
                return of({ status: false, message: methodNotAllowedMsg });
            }
            case 500:
                const serverErrorMsg = this.translate.instant('notifications.common.somethingWentWrong');
                this.toast.error(serverErrorMsg);
                return of(null);
            default:
                // For 4xx/5xx, always emit status: false so consumers don't treat it as success; httpCode as in the
                // { status: false, httpCode } bodies, since Spring's own 400s (ProblemDetail) have no errorCode
                const errorBody = error.error && typeof error.error === 'object' ? error.error : {};
                const message = errorBody['message'] ?? error.message ?? 'An error occurred';
                return of({ ...errorBody, status: false, message, httpCode: errorBody['httpCode'] ?? error.status });
        }
    }

    getHostname(option?: string) {
        switch (option) {
            case 'WS_BASE_URL':
                return this.env.WS_BASE_URL;
            default:
                return this.env.API_URL;
        }
    }

    public get(path: string, params?: any, headers?: Record<string, string>, responseType?: any, hostnameOption = ''): Observable<HttpResponse<any> | null> {
        const url: string = this.getHostname(hostnameOption).concat(path);
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.get<any>(url, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public getByUrl(url: string, params?: any, headers?: Record<string, string>, responseType?: any): Observable<HttpResponse<any> | null> {
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.get<any>(url, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public post(path: string, body: any, params?: any, headers?: Record<string, string>, responseType?: any, hostnameOption = ''): Observable<HttpResponse<any> | null> {
        const url: string = this.getHostname(hostnameOption) + path;
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.post<any>(url, body, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public postByUrl(url: string, body: any, params?: any, headers?: Record<string, string>, responseType?: any): Observable<HttpResponse<any> | null> {
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.post<any>(url, body, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public put(path: string, body: any, params?: any, headers?: Record<string, string>, responseType?: any, hostnameOption = ''): Observable<HttpResponse<any> | null> {
        const url: string = this.getHostname(hostnameOption) + path;
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.put<any>(url, body, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public putByUrl(url: string, body: any, params?: any, headers?: Record<string, string>, responseType?: any): Observable<HttpResponse<any> | null> {
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.put<any>(url, body, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public patch(path: string, body: any, params?: any, headers?: Record<string, string>, responseType?: any, hostnameOption = ''): Observable<HttpResponse<any> | null> {
        const url: string = this.getHostname(hostnameOption) + path;
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.patch<any>(url, body, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public patchByUrl(url: string, body: any, params?: any, headers?: Record<string, string>, responseType?: any): Observable<HttpResponse<any> | null> {
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.patch<any>(url, body, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public delete(path: string, params?: any, headers?: Record<string, string>, responseType?: any, hostnameOption = ''): Observable<HttpResponse<any> | null> {
        const url: string = this.getHostname(hostnameOption) + path;
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.delete(url, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public deleteByUrl(url: string, params?: any, headers?: Record<string, string>, responseType?: any): Observable<HttpResponse<any> | null> {
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.delete(url, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public deleteWithBody(path: string, body: any, params?: any, headers?: Record<string, string>, responseType?: any, hostnameOption = ''): Observable<HttpResponse<any> | null> {
        const url: string = this.getHostname(hostnameOption) + path;
        const options = this.getHttpOptions(params, headers, responseType);
        return this.httpClient.delete(url, {...options, body}).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    /**
     * POST with FormData. Omits Content-Type so the browser sets multipart/form-data with boundary.
     */
    public postFormData(path: string, formData: FormData, params?: any, responseType?: any, hostnameOption = ''): Observable<HttpResponse<any> | null> {
        const url: string = this.getHostname(hostnameOption) + path;
        const options = this.getHttpOptions(params, { 'Content-Type': '' }, responseType);
        return this.httpClient.post<any>(url, formData, options).pipe(
            map((resp: any) => this.handleResponseBodyError(resp)),
            catchError(error => this.handleError(error))
        );
    }

    public exportFile(path: string, body: any, responseType?: any, headers?: Record<string, string>, hostnameOption = ''): Observable<Blob | null> {
        const url: string = this.getHostname(hostnameOption) + path;
        const options = this.getHttpOptions(body, headers, responseType);
        return this.httpClient.post<any>(url, body, options).pipe(
            map((resp: any) => {
                if (resp.size < 100) {
                    const errorMsg = this.translate.instant('notifications.common.noPermission');
                    this.toast.error(errorMsg);
                    return null;
                }
                return resp;
            }),
            catchError(error => this.handleError(error))
        );
    }

    /**
     * GET with full HttpResponse and Blob body (e.g. file download). Uses default API URL, token, and params from getHttpOptions.
     * On HTTP failure, same error handling as other methods (may emit null or { status: false, ... }).
     */
    public getBlobResponse(path: string, params?: any, headers?: Record<string, string>, hostnameOption = ''): Observable<HttpResponse<Blob> | any> {
        const url: string = this.getHostname(hostnameOption).concat(path);
        const opts = this.getHttpOptions(params, headers);
        return this.httpClient.get(url, {
            headers: opts.headers,
            params: opts.params,
            observe: 'response',
            responseType: 'blob'
        }).pipe(catchError(error => this.handleError(error)));
    }

}
